import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail } from "lucide-react";
import api from "@/lib/api";

export default function InlineNewsletter({ variant = "card" }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      toast.success(data?.message || "Subscribed!");
      setSubscribed(true);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const trialCta = (
    <a
      href="https://summarymaps.com/?utm_source=journal&utm_medium=newsletter"
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 text-sm font-medium text-brand hover:underline"
      data-testid="inline-newsletter-trial-cta"
    >
      Try Summary Maps free for 7 days <ArrowRight className="w-4 h-4" />
    </a>
  );

  if (variant === "compact") {
    if (subscribed) {
      return (
        <div className="max-w-lg" data-testid="inline-newsletter-success">
          <p className="text-sm text-ink">You're on the list — one weekly digest, every Monday.</p>
          <div className="mt-2">{trialCta}</div>
        </div>
      );
    }
    return (
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2 max-w-lg" data-testid="inline-newsletter-compact">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@reader.com"
          className="flex-1 min-w-0 px-4 py-2.5 rounded-full bg-white border border-ink/10 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
          data-testid="inline-newsletter-input"
        />
        <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-60" data-testid="inline-newsletter-submit">
          {submitting ? "…" : (<>Subscribe <ArrowRight className="w-4 h-4" /></>)}
        </button>
      </form>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand/15 bg-brand-soft/50 px-6 py-8 sm:px-10 sm:py-10" data-testid="inline-newsletter">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="max-w-md">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/70 text-brand text-[11px] font-medium">
            <Mail className="w-3.5 h-3.5" /> Mondays · one essay · zero noise
          </div>
          <h3 className="mt-3 font-serif text-2xl sm:text-3xl leading-tight text-ink">
            Enjoyed this? <span className="italic text-brand">Get the next one in your inbox.</span>
          </h3>
          <p className="mt-2 text-sm text-ink-muted">
            Editor-picked essays on mindmaps, education, and the books worth remembering.
          </p>
        </div>
        {subscribed ? (
          <div className="w-full sm:w-auto sm:min-w-[320px]" data-testid="inline-newsletter-success">
            <p className="text-sm font-medium text-ink">You're on the list — one weekly digest, every Monday.</p>
            <div className="mt-2">{trialCta}</div>
          </div>
        ) : (
        <form onSubmit={onSubmit} className="w-full sm:w-auto sm:min-w-[320px] flex flex-col sm:flex-row gap-2" data-testid="inline-newsletter-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@reader.com"
            className="flex-1 min-w-0 px-4 py-3 rounded-full bg-white border border-ink/10 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
            data-testid="inline-newsletter-input"
          />
          <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-60 whitespace-nowrap" data-testid="inline-newsletter-submit">
            {submitting ? "Subscribing…" : (<>Subscribe <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
